import React, { useState, useEffect } from "react";
import {
  Input,
  InputNumber,
  Button,
  Select,
  TimePicker,
  DatePicker,
  Card,
  Tag,
  Badge,
  Switch,
  Checkbox,
  Modal,
  Form,
  Space,
  message,
  Tooltip,
} from "antd";
import {
  DeleteOutlined, PlusOutlined, MinusOutlined,
  ClockCircleOutlined, PlayCircleOutlined, StopOutlined,
  DownOutlined, UpOutlined, EditOutlined, StarOutlined, StarFilled,
  InboxOutlined, UndoOutlined,
} from "@ant-design/icons";
//import moment from "moment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import styles from "./WeekTemplate.module.css";
import {
  getUserWeekTemplateSchedulesInOrg,
  saveUserWeekTemplateSchedule,
  deleteUserWeekTemplateSchedule,
  createTemplateThunk,
  updateTemplateThunk,
  setTemplateStatusThunk,
  setTemplateDefaultThunk,
  archiveTemplatesThunk,
  unarchiveTemplatesThunk,
} from '../redux/slieces/weekTemplateSlice';
import { useDispatch, useSelector } from 'react-redux';
import { getUsersInOrganisation } from '../redux/slieces/userSlice';
import { getOrganizationSettings } from '../redux/slieces/organizationSettingsSlice';
import { getCurrentSubOrganisations } from '../redux/slieces/subOrganisationSlice';
import { useKeycloak } from '@react-keycloak/web';
import logger from "../core/utils/logger";
import { useTranslation } from 'react-i18next';
import { WorkHoursShouldCalcuated } from "../core/weekplan/weekplanRules";
import { sortWorkers } from '../utils/workerSorting';
import { getColorFromStatus, getTextColorForStatus } from "../core/utils/weekPlanUtils";
import { localizeTemplateError } from "../core/utils/templateErrors";
const { Option } = Select;

// Updated days array with full English names
const days = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
const dayStatuses = [
  "Work",
  "Holiday",
  "Weekend",
  "Sick",
  "Off",
  "TimeCompensation",
  "EmergencyService",
  "ChildSick",
  "PublicHoliday",
];

// Date-only display/parse format for validity + creation dates.
const DATE_FORMAT = "YYYY-MM-DD";

// Dummy translation function.
const getTranslation = (key, culture) => key;
// Extend dayjs with UTC support
dayjs.extend(utc);
dayjs.extend(customParseFormat); // ✅ Add this to support isValid()

// Convert time value to dayjs local time (for display in TimePicker)
const toLocalTime = (timeValue) => {
    if (!timeValue) return null;

    const isValid = dayjs(timeValue).isValid();
    if (!isValid) return null;

    // Check if it's already a Date object (local time) or a UTC string
    if (timeValue instanceof Date) {
        // It's already a local Date object, just convert to dayjs
        return dayjs(timeValue);
    }

    // If it's a string, check if it has timezone information
    if (typeof timeValue === 'string') {
        const hasZ = timeValue.includes('Z');
        const hasOffset = timeValue.includes('+') || timeValue.match(/-\d{2}:/);

        if (hasZ || hasOffset) {
            // Has timezone info, treat as UTC and convert to local
            return dayjs.utc(timeValue).local();
        } else {
            // No timezone info, assume it's already local
            return dayjs(timeValue);
        }
    }

    // Fallback: treat as local time
    return dayjs(timeValue);
};

// Parse a DATE-ONLY value (validFrom/validTo/createdAt) at day granularity.
// Deliberately NOT toLocalTime — validity dates are calendar dates and must NOT
// pass through the shift-time UTC↔local conversion (would drift at day boundaries
// vs. mobile). We strip any time/zone portion and read only the YYYY-MM-DD.
const toCalendarDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    // Already a JS Date (e.g. from a stale path) — read its calendar day as-is.
    return dayjs(value).startOf('day');
  }
  if (typeof value === 'string') {
    // Take just the date portion so an accidental "2026-01-31T00:00:00Z" never
    // shifts the day under a negative UTC offset.
    const datePart = value.split('T')[0];
    const parsed = dayjs(datePart, DATE_FORMAT, true);
    return parsed.isValid() ? parsed : null;
  }
  // dayjs object
  const d = dayjs(value);
  return d.isValid() ? d.startOf('day') : null;
};

// Render a date-only value as a localized calendar string (no TZ shift).
const formatCalendarDate = (value) => {
  const d = toCalendarDate(value);
  return d ? d.format(DATE_FORMAT) : "—";
};

// Helper function: Translate title from English database value to current language
const translateTitle = (title, t) => {
  if (!title) return '';

  const titleTranslations = {
    'Mr.': t('register.mr'),
    'Mrs.': t('register.mrs'),
    'Ms.': t('register.ms'),
    'Mx.': t('register.mx'),
    // Academic/Medical titles are usually the same across languages
    'Dr.': 'Dr.',
    'Prof.': 'Prof.',
    'Prof. Dr.': 'Prof. Dr.',
    'Dr. med.': 'Dr. med.',
    'Prof. Dr. med.': 'Prof. Dr. med.'
  };

  return titleTranslations[title] || title;
};

// Helper function: Format employee display name by looking up user data
const formatEmployeeName = (employee, users, t) => {
  // Look up the user by email from the users array
  const user = users && users.find(u => u.email === employee.email);

  if (user && (user.firstName || user.surname)) {
    // If we found the user and they have personal info, use it
    const parts = [
      translateTitle(user.title?.trim(), t),
      user.firstName?.trim(),
      user.surname?.trim()
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(' ') : user.name || employee.name || 'Unknown User';
  }

  // Fallback to employee name field for existing users or if user not found
  return employee.name || 'Unknown User';
};

// Helper mapping functions
const mapIndexToStatus = (index) => dayStatuses[index] || "Work";
const mapStatusToIndex = (status) => dayStatuses.indexOf(status);

// Helper function to create a default work duration
const createDefaultWorkDuration = (startTime = "08:00", endTime = "12:00") => {
  const defaultDate = "2025-01-01";
  // Parse as local time and convert to UTC properly
  const startLocal = dayjs(`${defaultDate} ${startTime}`, "YYYY-MM-DD HH:mm");
  const endLocal = dayjs(`${defaultDate} ${endTime}`, "YYYY-MM-DD HH:mm");
  return {
    startTime: dayjs.utc(startLocal.valueOf()).toISOString(),
    endTime: dayjs.utc(endLocal.valueOf()).toISOString(),
  };
};

// Helper function to suggest next time slot based on existing durations
const suggestNextTimeSlot = (existingDurations) => {
  if (!existingDurations || existingDurations.length === 0) {
    return { startTime: "08:00", endTime: "12:00" };
  }

  // Sort durations by start time and find the latest end time
  const sortedDurations = existingDurations
    .filter(d => d.startTime && d.endTime)
    .sort((a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf());

  if (sortedDurations.length === 0) {
    return { startTime: "08:00", endTime: "12:00" };
  }

  const lastDuration = sortedDurations[sortedDurations.length - 1];
  const lastEndTime = toLocalTime(lastDuration.endTime);

  // Suggest starting 1 hour after the last end time
  const suggestedStart = lastEndTime.add(1, 'hour');
  const suggestedEnd = suggestedStart.add(4, 'hour'); // Default 4-hour duration

  return {
    startTime: suggestedStart.format('HH:mm'),
    endTime: suggestedEnd.format('HH:mm')
  };
};

// Validation function for work duration times
const validateWorkDuration = (duration, allDurations, currentIndex) => {
  const errors = [];

  if (!duration.startTime || !duration.endTime) {
    errors.push("Both start and end times are required");
    return errors;
  }

  const startTime = toLocalTime(duration.startTime);
  const endTime = toLocalTime(duration.endTime);

  // Check if start time is before end time
  if (startTime.isAfter(endTime) || startTime.isSame(endTime)) {
    errors.push("Start time must be before end time");
  }

  // Check for overlaps with other durations
  for (let i = 0; i < allDurations.length; i++) {
    if (i === currentIndex) continue; // Skip self

    const otherDuration = allDurations[i];
    if (!otherDuration.startTime || !otherDuration.endTime) continue;

    const otherStart = toLocalTime(otherDuration.startTime);
    const otherEnd = toLocalTime(otherDuration.endTime);

    // Check for overlap
    if (
      (startTime.isBefore(otherEnd) && endTime.isAfter(otherStart)) ||
      (startTime.isSame(otherStart)) || (endTime.isSame(otherEnd))
    ) {
      errors.push(`Time overlaps with another work period (${otherStart.format('HH:mm')} - ${otherEnd.format('HH:mm')})`);
      break;
    }
  }

  // Check reasonable working hours (between 5:00 and 23:00)
  if (startTime.hour() < 5 || endTime.hour() > 23) {
    errors.push("Working hours should be between 05:00 and 23:00");
  }

  // Check maximum duration (12 hours)
  const durationHours = endTime.diff(startTime, 'hour', true);
  if (durationHours > 12) {
    errors.push("Maximum work duration is 12 hours");
  }

  // Check minimum duration (15 minutes)
  if (durationHours < 0.25) {
    errors.push("Minimum work duration is 15 minutes");
  }

  return errors;
};

// Function to calculate total daily working hours
const calculateDailyHours = (workDurations) => {
  if (!workDurations || workDurations.length === 0) return 0;

  let totalMinutes = 0;
  workDurations.forEach(duration => {
    if (duration.startTime && duration.endTime) {
      const start = toLocalTime(duration.startTime);
      const end = toLocalTime(duration.endTime);
      totalMinutes += end.diff(start, 'minute');
    }
  });

  return totalMinutes / 60; // Return hours as decimal
};

// Function to calculate total planned work time for the entire week
const calculateWeeklyPlannedTime = (calendarTemplate) => {
  if (!calendarTemplate || !calendarTemplate.weekPlan || !calendarTemplate.weekPlan.dailyPlans) {
    return { hours: 0, minutes: 0 };
  }

  let totalMinutes = 0;

  calendarTemplate.weekPlan.dailyPlans.forEach(dailyPlan => {
    if (dailyPlan.plan && dailyPlan.plan.workDurations) {
      // Only include planned time if the day status is Work, Holiday, Sick, or ChildSick
      const dayStatus = mapIndexToStatus(dailyPlan.plan.dayStatus);

      if (WorkHoursShouldCalcuated(dayStatus)) {
        dailyPlan.plan.workDurations.forEach(duration => {
          if (duration.startTime && duration.endTime) {
            const start = toLocalTime(duration.startTime);
            const end = toLocalTime(duration.endTime);
            if (start && end && end.isAfter(start)) {
              totalMinutes += end.diff(start, 'minute');
            }
          }
        });
      }
    }
  });

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return { hours, minutes };
};

// Function to calculate difference between expected and planned time
const calculateTimeDifference = (expectedHours, expectedMinutes, plannedHours, plannedMinutes) => {
  const expectedTotalMinutes = (expectedHours || 0) * 60 + (expectedMinutes || 0);
  const plannedTotalMinutes = (plannedHours || 0) * 60 + (plannedMinutes || 0);
  const differenceMinutes = plannedTotalMinutes - expectedTotalMinutes;

  const sign = differenceMinutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(differenceMinutes);
  const diffHours = Math.floor(absMinutes / 60);
  const diffMins = absMinutes % 60;

  return {
    sign,
    hours: diffHours,
    minutes: diffMins,
    isPositive: differenceMinutes >= 0
  };
};

const WeekTemplate = () => {
  const [employeeTemplateList, setEmployeeTemplateList] = useState([]);
  const [searchName, setSearchName] = useState("");
  // 27.4: template-level filters.
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  const [validityDate, setValidityDate] = useState(null);  // dayjs date-only, or null
  const [showArchived, setShowArchived] = useState(false);
  // 27.5: bulk-archive selection (set of calendar-template ids).
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [savingDuration, setSavingDuration] = useState(false);

  // 27.2: create/edit modal state. `editingTemplate` holds a working copy of the
  // CalendarTemplate being created or edited; `editingEmployeeId` is its owner.
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [templateForm] = Form.useForm();

  // Initialize all time sections as expanded (matching WeekPlan)
  const [collapsedTimeSections, setCollapsedTimeSections] = useState(() => {
    const initialState = {};
    days.forEach(day => {
      initialState[day] = false; // false = expanded
    });
    return initialState;
  });
  const dispatch = useDispatch();
  const { keycloak, initialized } = useKeycloak();
  const {loading, error, weekTemplateSchedules}=useSelector((state)=>state.weekTemplate.WeekTemplateData);
  const currentCulture = "de-DE";
  const {loading:usersLoading, error:usersError, users}=useSelector((state)=>state.userData.users);
  const {loading:subOrgsLoading, error:subOrgsError, data:subOrganisations}=useSelector((state)=>state.subOrganisationData.subOrganisations);
  const organizationSettings = useSelector((state)=>state.organizationSettings.settings);
  const [selectedYear, setSelectedYear] = useState(new Date("2025")); // Default to current year
  const { t } = useTranslation();
  const adminOrgId=keycloak.tokenParsed && keycloak.tokenParsed.organisationId
  ? Array.isArray(keycloak.tokenParsed.organisationId)
    ? keycloak.tokenParsed.organisationId[0]
    : keycloak.tokenParsed.organisationId
  : null;

  // Check if user has admin role
  const isAdmin = keycloak.tokenParsed && keycloak.tokenParsed.realm_access &&
    keycloak.tokenParsed.realm_access.roles &&
    keycloak.tokenParsed.realm_access.roles.includes('mediadmin');

  // Early return if not initialized
  if (!initialized) {
    return <div className={styles.loader}>
      <div className={styles.spinner}></div>
      <p>{t("loadingAuth")}</p>
    </div>;
  }

  // Check admin access - return access denied if not admin
  if (!isAdmin) {
    return (
      <div className={styles.container}>
        <div className={styles.accessDenied}>
          <h2>{t("accessDenied")}</h2>
          <p>{t("adminAccessRequired")}</p>
          <p>{t("contactAdministrator")}</p>
        </div>
      </div>
    );
  }
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setPageLoading(true);

      // Fetch users first
      await dispatch(getUsersInOrganisation());

      // Fetch organization settings for sorting
      await dispatch(getOrganizationSettings());

      // Fetch sub-organisations
      await dispatch(getCurrentSubOrganisations());

      // Fetch templates AFTER users are available
      await dispatch(getUserWeekTemplateSchedulesInOrg());


      setPageLoading(false);
    };

    if (initialized) {
      fetchData();
    }
  }, [dispatch, initialized]);

  useEffect(() => {
    if (!weekTemplateSchedules || weekTemplateSchedules.length === 0) {
      // Reset employeeTemplateList when weekTemplateSchedules is null or empty
      setEmployeeTemplateList([]);
    } else {
      logger.log("Updated week template schedules:", weekTemplateSchedules);
      setEmployeeTemplateList(weekTemplateSchedules);
    }
  }, [weekTemplateSchedules]); // ⬅️ Re-runs every time templates change

  // Re-fetch the list from the server (used after every mutation / rejection so
  // the UI matches server truth — no stale optimistic state, per 27.3 AC #6).
  const refreshList = () => dispatch(getUserWeekTemplateSchedulesInOrg());


  const buildDefaultDailyPlans = () => {
    // Find the default sub-organisation if it exists
    const defaultSubOrg = subOrganisations?.find(org => org.isDefault === true);
    const defaultDate = "2025-01-01";
    return days.map((day) => ({
      day: day,
      subOrganisationId: defaultSubOrg ? defaultSubOrg.id : null,
      subOrganisation: defaultSubOrg || null,
      plan: {
        dayStatus:
          day === "Saturday" || day === "Sunday"
            ? mapStatusToIndex("Weekend")
            : mapStatusToIndex("Work"),
        workDurations: [
          {
            startTime: dayjs.utc(dayjs(`${defaultDate} 08:00`, "YYYY-MM-DD HH:mm").valueOf()).toISOString(),
            endTime: dayjs.utc(dayjs(`${defaultDate} 12:00`, "YYYY-MM-DD HH:mm").valueOf()).toISOString(),
          },
          {
            startTime: dayjs.utc(dayjs(`${defaultDate} 13:00`, "YYYY-MM-DD HH:mm").valueOf()).toISOString(),
            endTime: dayjs.utc(dayjs(`${defaultDate} 17:00`, "YYYY-MM-DD HH:mm").valueOf()).toISOString(),
          },
        ],
      },
    }));
  };

  const createNewCalendarTemplate = () => ({
    name: t("defaultTemplateName"),
    isActive: true,
    isDefault: true,
    isArchived: false,
    validFrom: null,
    validTo: null,
    weekPlan: { dailyPlans: buildDefaultDailyPlans() },
    year: selectedYear,
    workTimeHours: 40,
    workTimeMinutes: 0,
  });

  // 27.2: build a fresh, named, time-bounded CalendarTemplate for create.
  const createNewTemplate = (name, validFrom, validTo) => ({
    name: name || "",
    isActive: true,
    isDefault: false,
    isArchived: false,
    validFrom: validFrom || null,
    validTo: validTo || null,
    weekPlan: { dailyPlans: buildDefaultDailyPlans() },
    year: selectedYear,
    workTimeHours: 40,
    workTimeMinutes: 0,
  });


// Updated function to get users without a template
const getUserWithoutTemplate = (weekTemplateSchedules) => {
  if (!weekTemplateSchedules || !users) return []; // Ensure data exists

  const idUsers = new Set(weekTemplateSchedules.map(item => item.userId)); // Use Set for better performance
  return users.filter(user => !idUsers.has(user.id.toString()) && (user.status=="Active" || user.status=="Registered")); // Check if user exists in template list
};
// Updated function to create a template for users without a template
const createTemplateForUsers=async (usersWithoutTemplate)=>{
  var userTemplates=[];
  usersWithoutTemplate.forEach(user=>{
    const newUserTemplate={
      userId:user.id.toString(),
      name:user.name,
      email:user.email,

      organizationId:adminOrgId,
      calendarTemplates:[createNewCalendarTemplate()]
    };
    userTemplates.push({employeeTemplate:newUserTemplate});
  });
  await Promise.all(userTemplates.map(emp => dispatch(saveUserWeekTemplateSchedule(emp.employeeTemplate))));
  message.success(t("changesSaved"));

  // Fetch updated list from database
  await refreshList();
};

  // ---- Template-level filtering (27.4) ------------------------------------
  // The covers-date predicate is DATE-ONLY: a template covers `date` when
  // validFrom <= date <= validTo (open bounds when a side is null). Compared at
  // day granularity with NO TZ shift so results match the backend exactly.
  const templateCoversDate = (template, date) => {
    if (!date) return true;
    const target = date.startOf('day');
    const from = toCalendarDate(template.validFrom);
    const to = toCalendarDate(template.validTo);
    if (from && target.isBefore(from, 'day')) return false;
    if (to && target.isAfter(to, 'day')) return false;
    return true;
  };

  const templateMatchesFilters = (template, nameTerm) => {
    // Archived hidden unless the toggle is on (27.1 AC #2 / 27.4 AC #4).
    if (template.isArchived && !showArchived) return false;
    // Status filter (27.4 AC #2).
    if (statusFilter === "active" && !template.isActive) return false;
    if (statusFilter === "inactive" && template.isActive) return false;
    // Validity covers-date (27.4 AC #3).
    if (!templateCoversDate(template, validityDate)) return false;
    // Template-name substring (27.4 AC #1). Empty term = match all.
    if (nameTerm) {
      const tName = (template.name || "").toLowerCase();
      if (!tName.includes(nameTerm)) return false;
    }
    return true;
  };

  // For each employee, keep only the templates matching the active filters; an
  // employee whose every template is filtered out is dropped (mirrors 26.5).
  const nameTermLower = searchName.toLowerCase();
  const filteredEmployees = sortWorkers(
    employeeTemplateList
      .map((emp) => {
        const employeeNameMatches = (emp.name || "").toLowerCase().includes(nameTermLower);
        // The search term can match either the employee name OR a template name.
        const effectiveNameTerm = employeeNameMatches ? "" : nameTermLower;
        const templates = (emp.calendarTemplates || []).filter((tpl) =>
          templateMatchesFilters(tpl, effectiveNameTerm)
        );
        return { ...emp, _visibleTemplates: templates, _employeeNameMatches: employeeNameMatches };
      })
      .filter((emp) => emp._visibleTemplates.length > 0 || (nameTermLower && emp._employeeNameMatches)),
    users,
    organizationSettings
  );

  // ---- Per-template mutations ---------------------------------------------

  // 27.3: toggle active/inactive via the by-id update (carries the full DTO).
  const toggleTemplateStatus = async (employee, template) => {
    const dto = buildTemplateDTO(template, { isActive: !template.isActive });
    const result = await dispatch(setTemplateStatusThunk({ templateId: template.id, dto }));
    if (setTemplateStatusThunk.fulfilled.match(result)) {
      message.success(template.isActive ? t("templateDeactivated") : t("templateActivated"));
    } else {
      message.error(localizeTemplateError(result.payload, t));
    }
    await refreshList();
  };

  // 27.3: set as default.
  const setTemplateDefault = async (template) => {
    const result = await dispatch(setTemplateDefaultThunk(template.id));
    if (setTemplateDefaultThunk.fulfilled.match(result)) {
      message.success(t("templateSaved"));
    } else {
      message.error(localizeTemplateError(result.payload, t));
    }
    await refreshList();
  };

  // 27.5: archive a single template (the default is guarded before we get here).
  const archiveTemplate = async (template) => {
    if (template.isDefault) {
      message.warning(t("cannotArchiveDefault"));
      return;
    }
    const result = await dispatch(archiveTemplatesThunk([template.id]));
    if (archiveTemplatesThunk.fulfilled.match(result)) {
      message.success(t("templatesArchived"));
    } else {
      message.error(localizeTemplateError(result.payload, t));
    }
    await refreshList();
  };

  // 27.5: bulk archive the current selection (default ids are never selected).
  const archiveSelected = async () => {
    if (selectedTemplateIds.length === 0) {
      message.warning(t("selectTemplatesToArchive"));
      return;
    }
    const result = await dispatch(archiveTemplatesThunk(selectedTemplateIds));
    if (archiveTemplatesThunk.fulfilled.match(result)) {
      message.success(t("templatesArchived"));
    } else {
      message.error(localizeTemplateError(result.payload, t));
    }
    setSelectedTemplateIds([]);
    await refreshList();
  };

  // 27.5: restore (unarchive) a single template; re-applies the overlap rule.
  const restoreTemplate = async (template) => {
    const result = await dispatch(unarchiveTemplatesThunk([template.id]));
    if (unarchiveTemplatesThunk.fulfilled.match(result)) {
      message.success(t("templateRestored"));
    } else {
      message.error(localizeTemplateError(result.payload, t));
    }
    await refreshList();
  };

  const toggleTemplateSelected = (templateId, checked) => {
    setSelectedTemplateIds((prev) =>
      checked ? [...prev, templateId] : prev.filter((id) => id !== templateId)
    );
  };

  // ---- Create / edit modal (27.2) -----------------------------------------

  const openCreateModal = (employee) => {
    const fresh = createNewTemplate("", null, null);
    setEditingTemplate(fresh);
    setEditingEmployeeId(employee.id);
    setIsCreating(true);
    templateForm.setFieldsValue({ name: "", validFrom: null, validTo: null });
    setModalOpen(true);
  };

  const openEditModal = (employee, template) => {
    // Deep clone so in-modal edits don't mutate the list until save.
    const working = JSON.parse(JSON.stringify(template));
    setEditingTemplate(working);
    setEditingEmployeeId(employee.id);
    setIsCreating(false);
    templateForm.setFieldsValue({
      name: template.name || "",
      validFrom: toCalendarDate(template.validFrom),
      validTo: toCalendarDate(template.validTo),
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingTemplate(null);
    setEditingEmployeeId(null);
    templateForm.resetFields();
  };

  // Build the wire DTO for a template, merging optional overrides. Validity
  // dates go out as plain YYYY-MM-DD strings (date-only, no TZ conversion).
  const buildTemplateDTO = (template, overrides = {}) => {
    const validFrom =
      overrides.validFrom !== undefined ? overrides.validFrom : template.validFrom;
    const validTo =
      overrides.validTo !== undefined ? overrides.validTo : template.validTo;
    return {
      id: template.id,
      name: overrides.name !== undefined ? overrides.name : (template.name || ""),
      isActive: overrides.isActive !== undefined ? overrides.isActive : !!template.isActive,
      validFrom: serializeDateOnly(validFrom),
      validTo: serializeDateOnly(validTo),
      year: template.year,
      workTimeHours: template.workTimeHours || 0,
      workTimeMinutes: template.workTimeMinutes || 0,
      weekPlan: template.weekPlan,
    };
  };

  // Serialize a validity value to a date-only "YYYY-MM-DD" string. Accepts a
  // dayjs object (from the DatePicker), a Date, or a string. Returns null for
  // empty. Never calls toISOString()/UTC — that is the shift-time path.
  const serializeDateOnly = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value.split('T')[0];
    const d = dayjs(value);
    return d.isValid() ? d.format(DATE_FORMAT) : null;
  };

  const saveTemplateFromModal = async () => {
    let values;
    try {
      values = await templateForm.validateFields();
    } catch (e) {
      return; // inline Form.Item errors already shown (name required / range)
    }

    const dto = buildTemplateDTO(editingTemplate, {
      name: values.name,
      validFrom: values.validFrom || null,
      validTo: values.validTo || null,
    });

    let result;
    if (isCreating) {
      result = await dispatch(createTemplateThunk({ employeeTemplateId: editingEmployeeId, dto }));
      if (createTemplateThunk.fulfilled.match(result)) {
        message.success(t("templateCreated"));
        closeModal();
      } else {
        message.error(localizeTemplateError(result.payload, t));
      }
    } else {
      result = await dispatch(updateTemplateThunk({ templateId: editingTemplate.id, dto }));
      if (updateTemplateThunk.fulfilled.match(result)) {
        message.success(t("templateSaved"));
        closeModal();
      } else {
        message.error(localizeTemplateError(result.payload, t));
      }
    }
    await refreshList();
  };

  // Mutate the in-modal editing template's 7-day pattern.
  const updateEditingTemplate = (updater) => {
    setEditingTemplate((prev) => (prev ? updater(prev) : prev));
  };

  // Toggle time section visibility (matching WeekPlan functionality)
  const toggleTimeSection = (day) => {
    setCollapsedTimeSections(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  // ---- 7-day editor handlers (operate on the in-modal editing template) ----

  const handleWorkTimeHoursChange = (hours) => {
    updateEditingTemplate((tpl) => ({ ...tpl, workTimeHours: hours || 0 }));
  };

  const handleWorkTimeMinutesChange = (minutes) => {
    updateEditingTemplate((tpl) => ({ ...tpl, workTimeMinutes: minutes || 0 }));
  };

  const handleTimeChange = (day, durationIndex, field, timeString) => {
    const defaultDate = "2025-01-01"; // Ensure a fixed date
    const localTime = dayjs(`${defaultDate} ${timeString}`, "YYYY-MM-DD HH:mm");
    if (!localTime.isValid()) {
      logger.error("Invalid date format:", timeString);
      message.error(t("invalidTimeFormat"));
      return;
    }
    const updatedTime = dayjs.utc(localTime.valueOf());

    updateEditingTemplate((tpl) => ({
      ...tpl,
      weekPlan: {
        ...tpl.weekPlan,
        dailyPlans: tpl.weekPlan.dailyPlans.map((plan) =>
          plan.day === day
            ? {
                ...plan,
                plan: {
                  ...plan.plan,
                  workDurations: plan.plan.workDurations.map((d, idx) =>
                    idx === durationIndex ? { ...d, [field]: updatedTime.toISOString() } : d
                  ),
                },
              }
            : plan
        ),
      },
    }));
  };

  const handleSubOrganisationChange = (day, subOrgId) => {
    updateEditingTemplate((tpl) => ({
      ...tpl,
      weekPlan: {
        ...tpl.weekPlan,
        dailyPlans: tpl.weekPlan.dailyPlans.map((plan) =>
          plan.day === day
            ? {
                ...plan,
                subOrganisationId: subOrgId === "none" ? null : parseInt(subOrgId),
                subOrganisation: subOrgId === "none" ? null : subOrganisations?.find(org => org.id === parseInt(subOrgId)) || null,
              }
            : plan
        ),
      },
    }));
  };

  const handleDayStatusChange = (day, value) => {
    updateEditingTemplate((tpl) => ({
      ...tpl,
      weekPlan: {
        ...tpl.weekPlan,
        dailyPlans: tpl.weekPlan.dailyPlans.map((plan) =>
          plan.day === day
            ? { ...plan, plan: { ...plan.plan, dayStatus: mapStatusToIndex(value) } }
            : plan
        ),
      },
    }));
  };

  const addWorkDuration = (day) => {
    updateEditingTemplate((tpl) => ({
      ...tpl,
      weekPlan: {
        ...tpl.weekPlan,
        dailyPlans: tpl.weekPlan.dailyPlans.map((plan) => {
          if (plan.day !== day) return plan;
          const existingDurations = plan.plan.workDurations || [];
          const { startTime, endTime } = suggestNextTimeSlot(existingDurations);
          const newDuration = createDefaultWorkDuration(startTime, endTime);
          return { ...plan, plan: { ...plan.plan, workDurations: [...existingDurations, newDuration] } };
        }),
      },
    }));
  };

  const removeWorkDuration = (day, durationIndex) => {
    updateEditingTemplate((tpl) => ({
      ...tpl,
      weekPlan: {
        ...tpl.weekPlan,
        dailyPlans: tpl.weekPlan.dailyPlans.map((plan) => {
          if (plan.day !== day) return plan;
          const existing = plan.plan.workDurations || [];
          if (existing.length <= 1) {
            message.warning(t("cannotDeleteLastDuration"));
            return plan;
          }
          return {
            ...plan,
            plan: { ...plan.plan, workDurations: existing.filter((_, idx) => idx !== durationIndex) },
          };
        }),
      },
    }));
  };

  // ---- Legacy whole-employee delete (true removal) ------------------------
  const deleteEmployee = async(id) => {
    await dispatch(deleteUserWeekTemplateSchedule(id));
    message.success(t("employeeDeleted"));
    await refreshList();
  };

  const cancelChanges = () => {
    setSearchName("");
    setStatusFilter("all");
    setValidityDate(null);
    setShowArchived(false);
    setSelectedTemplateIds([]);
    refreshList();
  };

  const createTemplate=()=>{
    var usersWithoutTemplate=getUserWithoutTemplate(weekTemplateSchedules);
    createTemplateForUsers(usersWithoutTemplate);
  }

  // ---- Per-template row (status, validity, default, actions, selection) ----
  const renderTemplateRow = (employee, template) => {
    const displayStatus = template.isActive ? t("active") : t("inactive");
    const selectable = !template.isDefault && !template.isArchived;
    const checked = selectedTemplateIds.includes(template.id);

    return (
      <div key={template.id} className={styles.templateRow}>
        <div className={styles.templateRowHeader}>
          <Space size="small" wrap>
            {selectable && (
              <Checkbox
                checked={checked}
                onChange={(e) => toggleTemplateSelected(template.id, e.target.checked)}
              />
            )}
            {template.isDefault && (
              <Tooltip title={t("cannotArchiveDefault")}>
                <Checkbox checked={false} disabled />
              </Tooltip>
            )}
            <span className={styles.templateName}>
              {template.name || t("noTemplates")}
            </span>
            {template.isDefault && (
              <Badge
                count={t("defaultBadge")}
                style={{ backgroundColor: "#52c41a" }}
              />
            )}
            <Tag color={template.isActive ? "green" : "default"}>{displayStatus}</Tag>
            {template.isArchived && <Tag color="orange">{t("archived")}</Tag>}
          </Space>
        </div>

        <div className={styles.templateRowMeta}>
          <span><strong>{t("createdDate")}:</strong> {formatCalendarDate(template.createdAt)}</span>
          <span><strong>{t("validFrom")}:</strong> {formatCalendarDate(template.validFrom)}</span>
          <span><strong>{t("validTo")}:</strong> {formatCalendarDate(template.validTo)}</span>
        </div>

        <div className={styles.templateRowActions}>
          <Space size="small" wrap>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(employee, template)}
            >
              {t("editTemplate")}
            </Button>

            {!template.isArchived && (
              <>
                <Tooltip title={template.isActive ? t("deactivateTemplate") : t("activateTemplate")}>
                  <Switch
                    size="small"
                    checked={template.isActive}
                    checkedChildren={t("active")}
                    unCheckedChildren={t("inactive")}
                    onChange={() => toggleTemplateStatus(employee, template)}
                  />
                </Tooltip>

                {!template.isDefault && (
                  <Button
                    size="small"
                    icon={<StarOutlined />}
                    onClick={() => setTemplateDefault(template)}
                  >
                    {t("setAsDefaultTemplate")}
                  </Button>
                )}
                {template.isDefault && (
                  <Tag icon={<StarFilled />} color="gold">{t("defaultBadge")}</Tag>
                )}

                <Tooltip title={template.isDefault ? t("cannotArchiveDefault") : ""}>
                  <Button
                    size="small"
                    icon={<InboxOutlined />}
                    disabled={template.isDefault}
                    onClick={() => archiveTemplate(template)}
                  >
                    {t("archived")}
                  </Button>
                </Tooltip>
              </>
            )}

            {template.isArchived && (
              <Button
                size="small"
                icon={<UndoOutlined />}
                onClick={() => restoreTemplate(template)}
              >
                {t("restore")}
              </Button>
            )}
          </Space>
        </div>
      </div>
    );
  };

  // ---- Per-employee card: header + list of template rows ------------------
  const renderEmployeeCard = (employee) => {
    const templates = employee._visibleTemplates || [];
    return (
      <Card key={employee.id} className={styles.largeCard}>
        <div className={styles.cardHeader}>
          <div className={styles.employeeNameDisplay}>
            {formatEmployeeName(employee, users, t)}
          </div>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openCreateModal(employee)}
              size="small"
            >
              {t("newTemplate")}
            </Button>
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={() => deleteEmployee(employee.id)}
              size="small"
            >
              {t("delete")}
            </Button>
          </Space>
        </div>

        {templates.length === 0 ? (
          <div className={styles.emptyTemplates}>{t("noTemplates")}</div>
        ) : (
          <div className={styles.templateList}>
            {templates.map((tpl) => renderTemplateRow(employee, tpl))}
          </div>
        )}
      </Card>
    );
  };

  // ---- The reused 7-day editor, scoped to the in-modal editing template ----
  const renderSevenDayEditor = () => {
    const tpl = editingTemplate;
    if (!tpl || !tpl.weekPlan) return null;
    return (
      <div className={styles.weekGrid}>
        {/* Epic 32: the expected weekly work time (Soll) is no longer set on the
            template — it comes from the employee's settings (Employee Management →
            Expected Weekly Work Time). Only the sum of the shift pattern is shown
            here. The template's workTimeHours/Minutes are kept unchanged in the DTO
            for backward compatibility. */}
        <div className={styles.workTimeSection}>
          <div className={styles.workTimeRow}>
            <span className={styles.workTimeRowLabel}>{t("sum")}:</span>
            <div className={styles.workTimeDisplay}>
              {(() => {
                const planned = calculateWeeklyPlannedTime(tpl);
                return `${planned.hours}h ${planned.minutes}min`;
              })()}
            </div>
          </div>
        </div>

        <div className={styles.dayColumns}>
        {days.map((day) => {
          const dailyPlan = tpl.weekPlan.dailyPlans.find((dp) => dp.day === day);
          const dayPlan = dailyPlan ? dailyPlan.plan : null;
          if (!dayPlan) return null;
          const displayStatus = dayPlan ? mapIndexToStatus(dayPlan.dayStatus) : "Work";

          return (
            <div
              key={day}
              className={styles.dayCell}
              style={{
                backgroundColor: getColorFromStatus(displayStatus),
                color: getTextColorForStatus(displayStatus)
              }}
            >
              <div className={styles.dayHeader}>{t(`days.${day.toLowerCase()}`)}</div>
              <div className={styles.dayStatus}>
                <label>{t("status")}:</label>
                <Select
                  value={displayStatus}
                  onChange={(value) => handleDayStatusChange(day, value)}
                  className={styles.dayStatusSelect}
                >
                  {dayStatuses.map((status) => (
                    <Option key={status} value={status}>
                      {t(`statuses.${status?.toLowerCase()}`)}
                    </Option>
                  ))}
                </Select>
              </div>
              <div className={styles.subOrgSelection}>
                <label>{t("subOrganisation")}:</label>
                <Select
                  value={dailyPlan?.subOrganisationId ? dailyPlan.subOrganisationId.toString() : "none"}
                  onChange={(value) => handleSubOrganisationChange(day, value)}
                  className={styles.subOrgSelect}
                  placeholder={t("selectSubOrganisation")}
                  allowClear
                >
                  <Option key="none" value="none">{t("noSubOrganisation")}</Option>
                  {subOrganisations?.map((subOrg) => (
                    <Option key={subOrg.id} value={subOrg.id.toString()}>
                      {subOrg.name}
                    </Option>
                  ))}
                </Select>
              </div>
              <div className={styles.timeSection}>
                <div className={styles.timeSectionHeader} onClick={() => toggleTimeSection(day)}>
                  <Tooltip title={t("workingHours")}>
                    <span className={styles.timeSectionTitle}>
                      <ClockCircleOutlined className={styles.iconLabel} />
                      <span className={styles.timeSectionText}>{t("workingHours")}</span>
                    </span>
                  </Tooltip>
                  {collapsedTimeSections[day] ? <DownOutlined /> : <UpOutlined />}
                </div>
                <div className={`${styles.timeSectionContent} ${collapsedTimeSections[day] ? styles.timeSectionCollapsed : styles.timeSectionExpanded}`}>
                  {dayPlan.workDurations.map((duration, idx) => (
                    <div key={idx} className={styles.duratationGruop}>
                      <div className={styles.durationIndex}>{idx + 1}</div>
                      <div className={styles.timeRowsContainer}>
                        <div className={styles.timeRow}>
                          <Tooltip title={t("start")}>
                            <PlayCircleOutlined className={styles.iconLabel} />
                          </Tooltip>
                          <TimePicker
                            format="HH:mm"
                            size="small"
                            value={toLocalTime(duration.startTime)}
                            onChange={(time, timeString) =>
                              handleTimeChange(day, idx, "startTime", timeString)}
                          />
                        </div>
                        <div className={styles.timeRow}>
                          <Tooltip title={t("end")}>
                            <StopOutlined className={styles.iconLabel} />
                          </Tooltip>
                          <TimePicker
                            format="HH:mm"
                            size="small"
                            value={toLocalTime(duration.endTime)}
                            onChange={(time, timeString) =>
                              handleTimeChange(day, idx, "endTime", timeString)}
                          />
                        </div>
                      </div>
                      <div className={styles.durationControlsWrapper}>
                        <div className={styles.durationControls}>
                          <Button
                            type="text"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => addWorkDuration(day)}
                            title={t("addWorkDuration")}
                          />
                          {dayPlan.workDurations.length > 1 && (
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<MinusOutlined />}
                              onClick={() => removeWorkDuration(day, idx)}
                              title={t("removeWorkDuration")}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!dayPlan.workDurations || dayPlan.workDurations.length === 0) && (
                    <Button
                      type="dashed"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => addWorkDuration(day)}
                      className={styles.addDurationButton}
                    >
                      {t("addWorkTime")}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        </div>
      </div>
    );
  };

  const archivableSelectedCount = selectedTemplateIds.length;

  return (
    <div className={styles.container}>
       {pageLoading ? (
       <div className={styles.loader}>
       <div className={styles.spinner}></div>
       <p>{t("loadingTemplates")}</p>
     </div>
    ) : (
      <>
      <h3 className={styles.title}>{t("weekTemplate")}</h3>
      <div className={styles.buttonsContainer}>
        <Button type="primary" onClick={createTemplate}>
          {t("createTemplate")}
        </Button>
        <Button
          type="primary"
          danger
          icon={<InboxOutlined />}
          disabled={archivableSelectedCount === 0}
          onClick={archiveSelected}
        >
          {t("archiveSelected")}{archivableSelectedCount > 0 ? ` (${archivableSelectedCount})` : ""}
        </Button>
        <Button onClick={cancelChanges}>{t("cancel")}</Button>
      </div>

      {/* 27.4: search + filters */}
      <div className={styles.search}>
        <Space wrap>
          <Input
            placeholder={t("searchTemplate")}
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            style={{ width: 220 }}
            allowClear
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 160 }}
          >
            <Option value="all">{t("allStatuses")}</Option>
            <Option value="active">{t("active")}</Option>
            <Option value="inactive">{t("inactive")}</Option>
          </Select>
          <DatePicker
            placeholder={t("validityCoversDate")}
            format={DATE_FORMAT}
            value={validityDate}
            onChange={(date) => setValidityDate(date)}
            allowClear
          />
          <Space size="small">
            <span>{t("showArchived")}</span>
            <Switch checked={showArchived} onChange={setShowArchived} />
          </Space>
        </Space>
      </div>

      {filteredEmployees.length === 0 ? (
        <div className={styles.emptyTemplates}>{t("noTemplates")}</div>
      ) : (
        <div className={styles.largeContainer}>
          {filteredEmployees.map((emp) => renderEmployeeCard(emp))}
        </div>
      )}

      {/* 27.2: create / edit modal with name + date-only DatePickers + 7-day editor */}
      <Modal
        title={isCreating ? t("createTemplate") : t("editTemplate")}
        open={modalOpen}
        onCancel={closeModal}
        onOk={saveTemplateFromModal}
        okText={t("saveTemplate")}
        cancelText={t("cancel")}
        width={isMobile ? "100%" : 1400}
        style={{ top: 24 }}
        styles={{ body: { maxHeight: "calc(100vh - 200px)", overflowY: "auto" } }}
        destroyOnClose
      >
        <Form form={templateForm} layout="vertical">
          <Form.Item
            name="name"
            label={t("templateName")}
            rules={[{ required: true, whitespace: true, message: t("nameRequired") }]}
          >
            <Input placeholder={t("templateName")} />
          </Form.Item>
          <Space wrap size="large">
            <Form.Item name="validFrom" label={t("validFrom")}>
              <DatePicker format={DATE_FORMAT} allowClear />
            </Form.Item>
            <Form.Item
              name="validTo"
              label={t("validTo")}
              dependencies={["validFrom"]}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const from = getFieldValue("validFrom");
                    // Date-only comparison: validTo must be on/after validFrom.
                    if (value && from && value.startOf('day').isBefore(from.startOf('day'), 'day')) {
                      return Promise.reject(new Error(t("validToBeforeValidFrom")));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker format={DATE_FORMAT} allowClear />
            </Form.Item>
          </Space>
        </Form>
        {renderSevenDayEditor()}
      </Modal>
      </>
      )}
    </div>
  );
};

export default WeekTemplate;
